// L'équipe gratuite (D-153, D-159, D-166), côté appareil : les fonctions de la base et la fiche FFBB du club lue d'ici
// (la FFBB refuse les serveurs, voir ffbb.ts).
import { supabase } from './supabase';
import { clubOrganismeRow, organismeLogo } from './ffbb';
import { reduceLogo } from './annuaire';
import { directoryRow, type DirectoryRow } from '../domain/annuaire';
import type { ClubState, FreeClub } from '../domain/gratuite';
import type { TeamDraft } from '../domain/console';

const db = () => {
  if (!supabase) throw new Error('Base non configurée');
  return supabase;
};
async function rpc<T>(fn: string, args: Record<string, unknown> = {}): Promise<T> {
  const { data, error } = await db().rpc(fn, args);
  if (error) throw Object.assign(new Error(error.message), { code: error.code });
  return data as T;
}

// --- Dans l'app -------------------------------------------------------------------------------------------------------
export const freeMyClubs = () => rpc<FreeClub[]>('free_my_clubs');
export const freeRequestAgain = (clubId: string) => rpc<{ open: boolean }>('free_request_again', { p_club: clubId });
export const freeEmailRefresh = (clubId: string, org: { code: string; email: string | null }) =>
  rpc<{ email: boolean; later?: boolean }>('free_club_email_refresh', { p_club: clubId, p_org: org });

// --- Sans compte : « Créer mon équipe » ----------------------------------------------------------------------------------
export interface TeamSignupView {
  contact_name: string; club_name: string | null; ffbb_code: string; expires_at: string; state: ClubState;
  logo: string | null; town: string | null; committee: string | null;
  teams: { name: string; coach: string | null; free: boolean; engagements: string[] }[];
}
export const teamSignupView = (token: string) => rpc<TeamSignupView | null>('team_signup_view', { p_token: token });
export interface TeamCreatePayload {
  club: Partial<DirectoryRow> & { name?: string };
  team: { name: string; category: string | null; gender: 'F' | 'M' | 'X'; links: TeamDraft['links'] };
}
export const teamSignupCreate = (token: string, p: TeamCreatePayload) =>
  rpc<{ club_id: string; team_id: string; free: boolean; invite: { token: string; expires_at: string } }>('team_signup_create', { p_token: token, p });

// --- Sans compte : « Votre club sur Arceau », valider, refuser ------------------------------------------------------------
export interface FreeViewTeam { id: string; name: string; gender: string | null; free: boolean | null; coach: string | null; since: string; players: number }
export interface FreeClubView {
  club_name: string; short_name: string | null; logo: string | null; color: string | null; ffbb_code: string | null; status: 'free' | 'validated' | 'refused';
  since: string; refused_at: string | null; purge_on: string | null; validated_at: string | null; teams: FreeViewTeam[];
  request: { status: string; created_at: string; expires_at: string } | null;
}
export const freeClubView = (token: string) => rpc<FreeClubView | null>('free_club_view', { p_token: token });
export const freeClubValidateLink = (token: string) => rpc<string>('free_club_validate_link', { p_token: token });
export const freeClubRefuse = (token: string, teamId: string) => rpc<{ refused: 'team' | 'club'; purge_on?: string }>('free_club_refuse', { p_token: token, p_team: teamId });
export interface ClubRequestView {
  club_name: string; short_name: string | null; color: string | null; ffbb_code: string | null; logo: string | null; town: string | null; committee: string | null; expires_at: string;
  teams: FreeViewTeam[]; engagements: string[];
}
export const clubRequestView = (token: string) => rpc<ClubRequestView | null>('club_request_view', { p_token: token });
export const clubRequestValidate = (token: string, p: { name: string; email: string; role: string; teams: TeamCreatePayload['team'][] }) =>
  rpc<{ club_id: string; added: string[]; trial_until: string; invite: { token: string; expires_at: string } }>('club_request_validate', { p_token: token, p });
export interface FreeLinkNote { free: true; status: string; team: string | null; gender: string | null; coach: string | null; under15: boolean; minor: boolean }
export const freeLinkNote = (token: string) => rpc<FreeLinkNote | null>('free_link_note', { p_token: token });

// --- La fiche FFBB du club, depuis l'appareil ---------------------------------------------------------------------------
/** La fiche de l'organisme par son code (annuaire D-094 : nom, commune, comité, e-mail, logo réduit à 128 px). */
export async function readClubOrg(code: string): Promise<DirectoryRow | null> {
  const row = directoryRow(await clubOrganismeRow(code));
  if (!row) return null;
  if (row.logo_ref) {
    try { const b = await organismeLogo(row.logo_ref); const l = b ? await reduceLogo(b) : null; if (l) return { ...row, logo: l }; } catch { /* sans logo */ }
  }
  return row;
}
